import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiUser, 
  FiBriefcase, 
  FiBook, 
  FiTarget, 
  FiTrendingUp,
  FiMail,
  FiPhone,
  FiMapPin,
  FiArrowRight,
  FiAward,
  FiCheckCircle
} from 'react-icons/fi';
import { api } from '../utils/api';
import { useRefreshListener, REFRESH_EVENTS } from '../utils/refreshEvents';
import './About.css';

const About = () => {
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAbout = useCallback(async () => {
    try {
      const res = await api.getAbout();
      setAbout(res.data);
    } catch (error) {
      console.error('Erreur lors du chargement des informations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAbout();
  }, [fetchAbout]);

  // Écouter les événements de rafraîchissement depuis l'admin
  useRefreshListener(REFRESH_EVENTS.ABOUT, fetchAbout);

  if (loading) {
    return (
      <div className="loading-page">
        <div className="loading-spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  if (!about) {
    return (
      <div className="about-page">
        <div className="page-header">
          <div className="page-header-background"></div>
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1>À propos</h1>
              <p>Informations en cours de chargement...</p>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  const iconMap = {
    FiTarget: FiTarget,
    FiTrendingUp: FiTrendingUp,
    FiAward: FiAward,
    FiCheckCircle: FiCheckCircle,
    FiBriefcase: FiBriefcase,
    FiBook: FiBook,
    FiUser: FiUser
  };

  return (
    <div className="about-page">
      {/* Hero Section */}
      <div className="page-header">
        <div className="page-header-background"></div>
        <div className="container">
          <motion.div
            className="about-hero"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="founder-profile-card">
              <div className="founder-image-wrapper">
                {about.founder?.image ? (
                  <motion.div
                    className="founder-image-container"
                    initial={{ opacity: 0, scale: 0.9, rotate: -5 }}
                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                    transition={{ duration: 0.8, delay: 0.2, type: "spring" }}
                  >
                    <img
                      src={about.founder.image}
                      alt={about.founder.name || 'Fondateur'}
                      className="founder-image"
                    />
                    <div className="founder-image-overlay"></div>
                  </motion.div>
                ) : (
                  <motion.div
                    className="founder-image-placeholder"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    <FiUser />
                  </motion.div>
                )}
              </div>
              
              <div className="founder-info-card">
                <motion.div
                  className="founder-badge"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <FiUser className="badge-icon" />
                  <span>Fondateur</span>
                </motion.div>
                
                <motion.h1
                  className="founder-name"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  {about.founder?.name || 'Fondateur'}
                </motion.h1>
                
                <motion.p
                  className="founder-title"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                >
                  {about.founder?.title || ''}
                </motion.p>
                
                {about.founder?.bio && (
                  <motion.p
                    className="founder-bio"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                  >
                    {about.founder.bio}
                  </motion.p>
                )}
                
                <motion.div
                  className="founder-contact-grid"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.7 }}
                >
                  {about.founder?.email && (
                    <motion.a
                      href={`mailto:${about.founder.email}`}
                      className="contact-card"
                      whileHover={{ scale: 1.05, y: -4 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="contact-icon-wrapper">
                        <FiMail />
                      </div>
                      <div className="contact-details">
                        <span className="contact-label">Email</span>
                        <span className="contact-value">{about.founder.email}</span>
                      </div>
                    </motion.a>
                  )}
                  {about.founder?.phone && (
                    <motion.a
                      href={`tel:${about.founder.phone.replace(/\s/g, '')}`}
                      className="contact-card"
                      whileHover={{ scale: 1.05, y: -4 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="contact-icon-wrapper">
                        <FiPhone />
                      </div>
                      <div className="contact-details">
                        <span className="contact-label">Téléphone</span>
                        <span className="contact-value">{about.founder.phone}</span>
                      </div>
                    </motion.a>
                  )}
                  {about.founder?.location && (
                    <motion.div
                      className="contact-card"
                      whileHover={{ scale: 1.05, y: -4 }}
                    >
                      <div className="contact-icon-wrapper">
                        <FiMapPin />
                      </div>
                      <div className="contact-details">
                        <span className="contact-label">Localisation</span>
                        <span className="contact-value">{about.founder.location}</span>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Parcours Professionnel */}
      {about.journey && (about.journey.introduction || (about.journey.experiences && about.journey.experiences.length > 0)) && (
        <section className="section">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="section-header">
                <FiBriefcase className="section-icon" />
                <h2>Parcours Professionnel</h2>
              </div>
              {about.journey.introduction && (
                <p className="section-intro">{about.journey.introduction}</p>
              )}
              {about.journey.experiences && about.journey.experiences.length > 0 && (
                <div className="timeline">
                  {about.journey.experiences
                    .sort((a, b) => (b.order || 0) - (a.order || 0))
                    .map((exp, index) => (
                      <motion.div
                        key={index}
                        className="timeline-item"
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        <div className="timeline-marker"></div>
                        <div className="timeline-content">
                          <div className="timeline-header">
                            <h3>{exp.title}</h3>
                            <span className="timeline-period">{exp.period}</span>
                          </div>
                          <p className="timeline-company">{exp.company}</p>
                          {exp.description && (
                            <p className="timeline-description">{exp.description}</p>
                          )}
                        </div>
                      </motion.div>
                    ))}
                </div>
              )}
            </motion.div>
          </div>
        </section>
      )}

      {/* Études et Formations */}
      {about.education && (about.education.introduction || (about.education.degrees && about.education.degrees.length > 0)) && (
        <section className="section section-alt">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="section-header">
                <FiBook className="section-icon" />
                <h2>Études et Formations</h2>
              </div>
              {about.education.introduction && (
                <p className="section-intro">{about.education.introduction}</p>
              )}
              {about.education.degrees && about.education.degrees.length > 0 && (
                <div className="education-grid">
                  {about.education.degrees
                    .sort((a, b) => (b.order || 0) - (a.order || 0))
                    .map((degree, index) => (
                      <motion.div
                        key={index}
                        className="education-card"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        whileHover={{ y: -5 }}
                      >
                        <div className="education-icon">
                          <FiAward />
                        </div>
                        <h3>{degree.title}</h3>
                        <p className="education-institution">{degree.institution}</p>
                        {degree.year && (
                          <span className="education-year">{degree.year}</span>
                        )}
                        {degree.description && (
                          <p className="education-description">{degree.description}</p>
                        )}
                      </motion.div>
                    ))}
                </div>
              )}
            </motion.div>
          </div>
        </section>
      )}

      {/* Missions et Valeurs */}
      {about.missions && (about.missions.introduction || (about.missions.values && about.missions.values.length > 0)) && (
        <section className="section">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="section-header">
                <FiTarget className="section-icon" />
                <h2>Missions et Valeurs</h2>
              </div>
              {about.missions.introduction && (
                <p className="section-intro">{about.missions.introduction}</p>
              )}
              {about.missions.values && about.missions.values.length > 0 && (
                <div className="values-grid">
                  {about.missions.values
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((value, index) => {
                      const IconComponent = iconMap[value.icon] || FiCheckCircle;
                      return (
                        <motion.div
                          key={index}
                          className="value-card"
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                          whileHover={{ y: -5, boxShadow: '0 12px 40px rgba(45, 80, 22, 0.15)' }}
                        >
                          <div className="value-icon">
                            <IconComponent />
                          </div>
                          <h3>{value.title}</h3>
                          <p>{value.description}</p>
                        </motion.div>
                      );
                    })}
                </div>
              )}
            </motion.div>
          </div>
        </section>
      )}

      {/* Ambitions */}
      {about.ambitions && (about.ambitions.introduction || (about.ambitions.goals && about.ambitions.goals.length > 0)) && (
        <section className="section section-alt">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="section-header">
                <FiTrendingUp className="section-icon" />
                <h2>Ambitions et Vision</h2>
              </div>
              {about.ambitions.introduction && (
                <p className="section-intro">{about.ambitions.introduction}</p>
              )}
              {about.ambitions.goals && about.ambitions.goals.length > 0 && (
                <div className="goals-list">
                  {about.ambitions.goals
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((goal, index) => (
                      <motion.div
                        key={index}
                        className="goal-item"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        <FiCheckCircle className="goal-icon" />
                        <div>
                          <h3>{goal.title}</h3>
                          {goal.description && (
                            <p>{goal.description}</p>
                          )}
                        </div>
                      </motion.div>
                    ))}
                </div>
              )}
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      {about.cta && (
        <section className="cta-section">
          <div className="container">
            <motion.div
              className="cta-content"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2>{about.cta.title || 'Prêt à collaborer ?'}</h2>
              <p>{about.cta.description || 'Contactez-moi pour discuter de votre projet'}</p>
              <Link to={about.cta.buttonLink || '/contact'} className="btn btn-primary btn-large">
                {about.cta.buttonText || 'Me contacter'}
                <FiArrowRight />
              </Link>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
};

export default About;

